<template>
  <div>
    <div :class="$style.modalContext">
      {{ $t('vendorsPage.vendorAddress') }}
    </div>
    <MCAVendorFieldset>
      <UiInput
        ref="vendorLine1Input"
        name="line1"
        type="text"
        :error-msg="$t('common.errors.mandatory')"
        :label="$t('common.address.addressLine1')"
        required
        :value="address.line1"
        @input="setVendorProps"
      />
      <UiInput
        ref="vendorLine2Input"
        name="line2"
        type="text"
        :label="$t('common.address.addressLine2')"
        :value="address.line2"
        @input="setVendorProps"
      />
      <UiInput
        name="country"
        type="text"
        :error-msg="$t('common.errors.mandatory')"
        :label="$t('common.address.country')"
        :value="countryName"
        size="small"
        required
        disabled
        @input="setVendorProps"
      />
      <!-- Beneficiary Address State -->
      <div v-if="requiredVendorFields.includes('beneficiaryStateOrProvince')">
        <UiDropdown
          v-if="stateOptions"
          ref="vendorStateInput"
          size="small"
          name="state"
          :external-label="stateName"
          data-cy="state"
          :label="
            $t('common.selectOption', {
              option: `${stateName} -`,
            })
          "
          :options="stateOptions"
          :value="address.state"
          required
          @input="setVendorProps"
        />
        <UiInput
          v-else
          ref="vendorStateInput"
          type="text"
          :error-msg="$t('common.errors.mandatory')"
          name="state"
          :label="stateName"
          :value="address.state"
          required
          @input="setVendorProps"
        />
      </div>

      <!-- Beneficiary Address City -->
      <UiInput
        v-if="requiredVendorFields.includes('beneficiaryCity')"
        ref="vendorCityInput"
        name="city"
        type="text"
        :error-msg="$t('common.errors.mandatory')"
        :label="cityName"
        :value="address.city"
        required
        @input="setVendorProps"
      />
      <!-- Beneficiary Address Postal Code -->
      <UiInput
        v-if="
          requiredVendorFields.includes('beneficiaryPostcode') &&
          selectedVendor.bankAddress.country &&
          !withoutPostalCodeCountries.includes(
            selectedVendor.bankAddress.country,
          )
        "
        ref="vendorPostalCodeInput"
        name="postalCode"
        type="text"
        :error-msg="$t('common.errors.mandatory')"
        :label="postalCode"
        :value="address.postalCode"
        required
        @input="setVendorProps"
      />
    </MCAVendorFieldset>
    <UiButton :text="$t('common.buttons.back')" @click="handlePrev" />
    <UiButton
      :text="$t('common.buttons.next')"
      data-cy="next-button"
      @click="handleNext"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { UiDropdown, UiButton, UiInput } from 'ui-components';
import {
  COUNTRY_CODE_WITH_NAMES,
  WITHOUT_POSTAL_CODE_COUNTRY_CODE,
} from '@/data/country-code-with-names';
import {
  stateOptionsForCountry,
  cityLabel,
  postalCodeLabel,
  stateLabel,
} from '@/utils/local';
import MCAVendorFieldset from './MCAVendorFieldset.vue';
export default {
  components: {
    UiButton,
    UiInput,
    UiDropdown,
    MCAVendorFieldset,
  },
  props: {
    requiredVendorFieldsWithValidation: { type: Object, required: true },
  },
  data() {
    return {
      address: {},
    };
  },
  mounted() {
    if (!this.selectedVendor.beneficiaryAddress) {
      return;
    }
    this.address = {
      line1: this.selectedVendor.beneficiaryAddress.line1,
      line2: this.selectedVendor.beneficiaryAddress.line2,
      country: this.selectedVendor.beneficiaryAddress.country,
      state: this.selectedVendor.beneficiaryAddress.state,
      city: this.selectedVendor.beneficiaryAddress.city,
      postalCode: this.selectedVendor.beneficiaryAddress.postalCode,
    };
  },
  computed: {
    ...mapGetters(['selectedVendor']),
    requiredVendorFields() {
      return Object.keys(this.requiredVendorFieldsWithValidation);
    },
    withoutPostalCodeCountries() {
      return WITHOUT_POSTAL_CODE_COUNTRY_CODE;
    },
    countryName() {
      const countryCode = this.selectedVendor?.beneficiaryAddress?.country;
      if (!countryCode) return '';
      return COUNTRY_CODE_WITH_NAMES[countryCode]();
    },
    stateOptions() {
      const country = this.selectedVendor?.beneficiaryAddress?.country;
      if (!country) return undefined;
      return stateOptionsForCountry(country);
    },
    stateName() {
      const country = this.selectedVendor?.beneficiaryAddress?.country;
      if (!country) return '';
      return stateLabel(country);
    },
    cityName() {
      if (!this.address.country) {
        return '';
      }
      return cityLabel(this.address.country);
    },
    postalCode() {
      const country = this.selectedVendor?.beneficiaryAddress?.country;
      if (!country) return '';
      return postalCodeLabel(country);
    },
  },
  methods: {
    setVendorProps(val, name) {
      if (name) {
        this.address[name] = val;
      }
    },
    handleNext(event) {
      if (!event) return;

      const inputs = [
        this.$refs.vendorLine1Input,
        this.$refs.vendorLine2Input,
        this.$refs.vendorStateInput,
        this.$refs.vendorCityInput,
        this.$refs.vendorPostalCodeInput,
      ].filter((input) => input);

      inputs.forEach((field) => field.$touch());
      const isValid = inputs.every((field) => field.isValid);
      if (isValid) {
        const addressData = {
          beneficiaryAddress: this.address,
        };
        this.$emit('next-tab', addressData);
      }
    },
    handlePrev(event) {
      if (event) {
        this.$emit('prev-tab');
      }
    },
  },
};
</script>
<style lang="less" module>
.modalContext {
  color: black;
  padding-bottom: 30px;
  text-align: center;
  font-weight: bold;
}
</style>
